/**
* Notary Public comparison cluster — /notary/compare/{slug}.
*
* ACCURACY POLICY (ตรวจ 2026-08-02)
* - ไทยใช้ระบบ "ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร" (Notarial Services
* Attorney) ตามข้อบังคับสภาทนายความ ไม่มีตำแหน่ง Notary Public ตามกฎหมายเฉพาะ
* - ค่าธรรมเนียมนิติกรณ์กรมการกงสุล: 200 บาท/ตราประทับ (ปกติ) และ
* 400 บาท/ตราประทับ (ด่วน) — ดู src/data/official-fees.ts
* - ไทยยังไม่ออก Apostille จนกว่าอนุสัญญาจะมีผล 28 ก.พ. 2027 (ภาคยานุวัติ
* 30 มิ.ย. 2026) — ดู src/data/legal-facts.ts / hcch.net
* - เวลาและค่าใช้จ่ายเป็น "ช่วงโดยประมาณ" การรับพิจารณาเป็นดุลพินิจของ
* หน่วยงานปลายทางเสมอ ไม่ระบุผลการพิจารณาล่วงหน้า
*/

export interface NotaryCompareRow {
  aspect: string;
  a: string;
  b: string;
}

export interface NotaryCompare {
  slug: string;
  title: string;
  description: string;
  h1: string;
  aLabel: string;
  bLabel: string;
  answer: string;
  rows: NotaryCompareRow[];
  chooseA: string[];
  chooseB: string[];
  faqs: Array<{ q: string; a: string }>;
  synonyms: string[];
}

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — ระเบียบของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันกับผู้รับเอกสารก่อนยื่นทุกครั้ง";

export const NOTARY_COMPARE: NotaryCompare[] = [
  {
    slug: "notary-vs-กงสุล-mfa",
    title: "รับรองโดยทนาย Notary กับ รับรองกรมการกงสุล ต่างกันอย่างไร",
    description:
      "เทียบทีละข้อระหว่างคำรับรองของทนายกับนิติกรณ์กรมการกงสุล ว่าใครรับรองอะไร ใช้กับปลายทางไหน ต้องทำต่อกันหรือไม่ พร้อมเช็กลิสต์ก่อนยื่น",
    h1: "ทนาย Notary vs กรมการกงสุล (MFA) — ต่างกันอย่างไร",
    aLabel: "คำรับรองโดยทนาย (Notarial Services Attorney)",
    bLabel: "นิติกรณ์กรมการกงสุล (MFA)",
    answer:
      "ทนายรับรอง “ลายมือชื่อ สำเนา หรือคำสาบานที่เกิดต่อหน้าทนาย” ส่วนกรมการกงสุลรับรอง “ลายมือชื่อของผู้ลงนามในเอกสารราชการหรือของทนายที่จดแจ้งไว้” จึงเป็นคนละชั้นของสายรับรอง ในหลายเคสต้องทำต่อกันเป็นลำดับ คือทนายก่อน แล้วจึงยื่นกงสุล ไม่ใช่เลือกอย่างใดอย่างหนึ่ง หน่วยงานปลายทางเป็นผู้กำหนดว่าต้องถึงชั้นไหน",
    rows: [
      { aspect: "รับรองอะไร", a: "ลายมือชื่อ / สำเนาถูกต้อง / คำสาบานที่ทำต่อหน้าทนาย", b: "ความถูกต้องของลายมือชื่อและตราของผู้ออกเอกสารหรือของทนายที่จดแจ้ง" },
      { aspect: "ผู้รับรอง", a: "ทนายความที่ขึ้นทะเบียนกับสภาทนายความแห่งประเทศไทย", b: "กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ" },
      { aspect: "ใช้กับเอกสารแบบใด", a: "เอกสารเอกชน เช่น POA, Affidavit, สำเนาพาสปอร์ต, สัญญา", b: "เอกสารราชการไทย และเอกสารเอกชนที่ผ่านทนายมาก่อน" },
      { aspect: "ค่าธรรมเนียมราชการ", a: "ไม่มีค่าธรรมเนียมราชการ มีเฉพาะค่าวิชาชีพทนาย", b: "200 บาท/ตราประทับ (ปกติ) หรือ 400 บาท/ตราประทับ (ด่วน)" },
      { aspect: "ระยะเวลาโดยประมาณ", a: "ทำเสร็จได้ภายในวันเดียวหากเอกสารพร้อม", b: "แบบปกติราว 3 วันทำการ แบบด่วนรับได้ในวันเดียวกัน ขึ้นกับคิว" },
      { aspect: "ต้องมาด้วยตนเองไหม", a: "ต้องเซ็นต่อหน้าทนาย จึงต้องมาเอง", b: "ยื่นแทนได้ ใช้เอกสารมอบฉันทะตามที่กรมการกงสุลกำหนด" },
    ],
    chooseA: [
      "ปลายทางระบุเพียงว่าต้อง notarised / certified true copy",
      "เอกสารเอกชนที่ต้องมีพยานลงนาม เช่น POA, Affidavit",
      "ยังไม่ทราบปลายทางแน่ชัด และต้องการเริ่มจากชั้นแรกที่จำเป็นเสมอ",
    ],
    chooseB: [
      "ปลายทางระบุว่าต้องผ่านการรับรองจากกระทรวงการต่างประเทศ",
      "ต้องนำเอกสารไปรับรองต่อที่สถานทูตประเทศปลายทาง",
      "เป็นเอกสารราชการไทย เช่น ทะเบียนบ้าน สูติบัตร ที่ต้องใช้ต่างประเทศ",
    ],
    faqs: [
      { q: "ต้องทำทั้งสองอย่างเสมอไหม?", a: "ไม่เสมอไป บางปลายทางรับเฉพาะคำรับรองของทนาย บางปลายทางบังคับให้ผ่านกงสุลและสถานทูตด้วย ให้ยึดข้อกำหนดเป็นลายลักษณ์อักษรของผู้รับเอกสาร " + TAIL },
      { q: "กงสุลรับรองเอกสารที่ทนายไม่ได้จดแจ้งลายมือชื่อได้ไหม?", a: "ไม่ได้ กรมการกงสุลเทียบลายมือชื่อทนายกับตัวอย่างที่จดแจ้งไว้ หากทนายไม่ได้จดแจ้งหรือหนังสือรับรองหมดอายุ เอกสารจะถูกตีกลับ" },
      { q: "ทำกงสุลก่อนแล้วค่อยให้ทนายรับรองได้ไหม?", a: "โดยทั่วไปไม่ได้ผล เพราะลำดับของสายรับรองต้องไล่จากผู้จัดทำเอกสารขึ้นไปหาหน่วยงานที่สูงกว่า การสลับลำดับมักทำให้ต้องเริ่มใหม่" },
    ],
    synonyms: ["notary กับ กงสุล ต่างกันยังไง", "รับรองเอกสาร กระทรวงการต่างประเทศ vs ทนาย", "นิติกรณ์ คือ", "mfa legalization thailand"],
  },
  {
    slug: "notary-vs-apostille",
    title: "Notary กับ Apostille ต่างกันอย่างไร ไทยออก Apostille ได้แล้วหรือยัง",
    description:
      "อธิบายความต่างระหว่างคำรับรองของทนายกับตรา Apostille พร้อมสถานะล่าสุดของไทยที่อนุสัญญาจะมีผล 28 ก.พ. 2027 และสิ่งที่ต้องทำในช่วงก่อนหน้านั้น",
    h1: "Notary vs Apostille — ต่างกันอย่างไร และไทยอยู่ตรงไหน",
    aLabel: "คำรับรองโดยทนาย (Notary)",
    bLabel: "ตรา Apostille",
    answer:
      "Notary เป็นชั้นแรกที่รับรองลายมือชื่อหรือสำเนา ส่วน Apostille เป็นตรารับรองระหว่างประเทศที่ใช้แทนการรับรองซ้อนหลายชั้นระหว่างรัฐภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 ปัจจุบันประเทศไทยยังออก Apostille ไม่ได้ เพราะยื่นภาคยานุวัติสารเมื่อ 30 มิถุนายน 2026 และอนุสัญญาจะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2027 ก่อนวันนั้นเอกสารไทยยังต้องใช้สายเดิมคือ ทนาย/ผู้ออกเอกสาร → กรมการกงสุล → สถานทูตปลายทางเมื่อจำเป็น",
    rows: [
      { aspect: "สถานะในไทยตอนนี้", a: "ใช้ได้ตามปกติ", b: "ยังออกไม่ได้ จนกว่าอนุสัญญามีผล 28 ก.พ. 2027" },
      { aspect: "รับรองอะไร", a: "ลายมือชื่อ สำเนา หรือคำสาบานต่อหน้าทนาย", b: "ความถูกต้องของลายมือชื่อ/ตราของเจ้าหน้าที่ผู้ออกเอกสาร ในกรอบอนุสัญญา" },
      { aspect: "ใช้กับประเทศใด", a: "การรับรองโดยทนายไม่จำกัดประเทศ แต่การยอมรับและขั้นตอนต่อไปขึ้นกับกฎของหน่วยงานผู้รับ", b: "เฉพาะระหว่างรัฐภาคีอนุสัญญาที่ไม่มีข้อคัดค้านระหว่างกัน" },
      { aspect: "จำนวนขั้นตอน", a: "1 ชั้น แล้วมักต่อด้วยกงสุลและสถานทูต", b: "ลดเหลือชั้นเดียวหลังอนุสัญญามีผล ไม่ต้องผ่านสถานทูตปลายทาง" },
      { aspect: "หน่วยงานผู้ออก", a: "ทนายความผู้ทำคำรับรองที่ขึ้นทะเบียนกับสภาทนายความ", b: "กรมการกงสุล กระทรวงการต่างประเทศ (Competent Authority ในอนาคต)" },
      { aspect: "แหล่งอ้างอิง", a: "ข้อบังคับสภาทนายความว่าด้วยการรับรองลายมือชื่อและเอกสาร", b: "HCCH — Thailand accedes to the 1961 Apostille Convention (30 มิ.ย. 2026)" },
    ],
    chooseA: [
      "ต้องใช้เอกสารก่อนวันที่ 28 ก.พ. 2027",
      "ปลายทางเป็นประเทศที่ไม่ใช่ภาคีอนุสัญญา",
      "เอกสารเอกชนที่ต้องมีผู้รับรองลายมือชื่อก่อนเข้าสู่สายราชการ",
    ],
    chooseB: [
      "วางแผนใช้เอกสารตั้งแต่ 28 ก.พ. 2027 เป็นต้นไป",
      "ปลายทางเป็นรัฐภาคีอนุสัญญาและระบุรับ Apostille",
      "ต้องการลดขั้นตอนสถานทูตปลายทาง",
    ],
    faqs: [
      { q: "ตอนนี้ขอ Apostille ที่ไทยได้ไหม?", a: "ยังไม่ได้ ไทยยื่นภาคยานุวัติสารเมื่อ 30 มิถุนายน 2026 และอนุสัญญาจะมีผลบังคับกับไทยในวันที่ 28 กุมภาพันธ์ 2027 ระหว่างนี้ยังต้องใช้สายรับรองเดิม " + TAIL },
      { q: "หลังไทยเข้าอนุสัญญาแล้ว ยังต้องใช้ทนาย Notary ไหม?", a: "ยังต้องใช้ในกรณีเอกสารเอกชน เพราะ Apostille รับรองลายมือชื่อของเจ้าหน้าที่ผู้ออกเอกสาร เอกสารเอกชนจึงยังต้องผ่านผู้รับรองที่รัฐยอมรับก่อน" },
      { q: "เอกสารที่รับรองก่อน 28 ก.พ. 2027 จะใช้ไม่ได้ไหม?", a: "โดยหลักยังใช้ได้ตามรูปแบบที่ปลายทางยอมรับในขณะยื่น แต่หากเอกสารมีอายุจำกัด เช่น หนังสือรับรองบริษัทหรือใบรับรองความประพฤติ ควรตรวจอายุเอกสารกับผู้รับก่อน" },
    ],
    synonyms: ["apostille ไทย เมื่อไหร่", "notary กับ apostille ต่างกัน", "ไทยเข้าอนุสัญญา apostille", "apostille thailand 2027"],
  },
  {
    slug: "notary-vs-นักแปล-naati",
    title: "ทนาย Notary กับ นักแปล NAATI ต่างกันอย่างไร ต้องใช้ทั้งคู่ไหม",
    description:
      "เทียบหน้าที่ของทนายผู้รับรองเอกสารกับนักแปลที่มี NAATI Certification ว่ารับรองคนละอย่างอย่างไร และกรณีใดที่ต้องใช้ทั้งสองอย่าง",
    h1: "ทนาย Notary vs นักแปล NAATI — ใครรับรองอะไร",
    aLabel: "ทนาย Notary",
    bLabel: "นักแปล NAATI Certified",
    answer:
      "ทนายรับรองตัวเอกสารหรือลายมือชื่อ ส่วนนักแปล NAATI รับรองว่าคำแปลตรงกับต้นฉบับ ถ้าปลายทางเป็นหน่วยงานออสเตรเลียหรือนิวซีแลนด์ที่ระบุ NAATI ไว้ชัดเจน มักใช้คำแปล NAATI อย่างเดียวได้ แต่ถ้าต้องยื่นทั้งในไทยและต่างประเทศ หรือปลายทางขอสำเนารับรองด้วย จึงต้องใช้ทั้งสองอย่างควบคู่กัน",
    rows: [
      { aspect: "รับรองอะไร", a: "ลายมือชื่อ / สำเนาถูกต้อง / คำสาบาน", b: "ความถูกต้องของคำแปล" },
      { aspect: "คุณสมบัติผู้รับรอง", a: "ทนายความขึ้นทะเบียนกับสภาทนายความแห่งประเทศไทย", b: "นักแปลที่ถือ NAATI Certification และมี Practitioner ID" },
      { aspect: "ตรวจสอบย้อนกลับ", a: "ตรวจเลขทะเบียนทนายกับสภาทนายความ", b: "ตรวจหมายเลขนักแปลได้ที่ naati.com.au" },
      { aspect: "ปลายทางที่นิยมใช้", a: "สถานทูต หน่วยงานราชการไทย คู่สัญญาต่างประเทศ", b: "Department of Home Affairs, Immigration New Zealand, AHPRA, VETASSESS, มหาวิทยาลัย" },
      { aspect: "รูปแบบผลงาน", a: "ตราประทับและถ้อยคำรับรองบนเอกสารต้นฉบับ/สำเนา", b: "คำแปลพร้อมตราและถ้อยคำรับรองของนักแปล ใช้ไฟล์ PDF ยื่นออนไลน์ได้ในหลายกรณี" },
      { aspect: "ต้นฉบับตัวจริง", a: "ทนายต้องเห็นต้นฉบับหรือผู้ลงนามต่อหน้า", b: "ส่วนใหญ่ใช้สแกนคุณภาพสูงได้ ขึ้นกับข้อกำหนดปลายทาง" },
    ],
    chooseA: [
      "ต้องรับรองสำเนาถูกต้องหรือลายมือชื่อบนเอกสารต้นฉบับ",
      "ปลายทางไม่ใช่ออสเตรเลีย/นิวซีแลนด์ และขอเอกสาร notarised",
      "ต้องนำเอกสารต่อไปยังกรมการกงสุลและสถานทูต",
    ],
    chooseB: [
      "ยื่นวีซ่า PR สัญชาติ หรือประเมินทักษะกับหน่วยงานออสเตรเลีย",
      "หน่วยงานระบุคำว่า NAATI certified translation",
      "ต้องการไฟล์คำแปลพร้อมยื่นออนไลน์",
    ],
    faqs: [
      { q: "แปลเองแล้วให้ทนายรับรองได้ไหม?", a: "ทนายรับรองได้เพียงลายมือชื่อของผู้แปลหรือสำเนาเอกสาร ไม่ได้รับรองว่าคำแปลถูกต้อง หน่วยงานที่ระบุ NAATI ไว้จึงมักไม่รับคำแปลที่ผู้ยื่นทำเอง " + TAIL },
      { q: "ไทยมีระบบ sworn translator ไหม?", a: "ประเทศไทยไม่มีระบบนักแปลสาบานตนที่รัฐแต่งตั้งแบบยุโรป การรับรองคำแปลในไทยจึงใช้ช่องทางกรมการกงสุลหรือผู้แปลที่ปลายทางยอมรับแทน" },
      { q: "ใช้ทั้ง NAATI และ Notary พร้อมกันได้ไหม?", a: "ได้ และเป็นเรื่องปกติเมื่อเอกสารชุดเดียวกันต้องใช้ทั้งกับหน่วยงานไทยและหน่วยงานออสเตรเลีย ควรทำสำเนาชุดแยกเพื่อไม่ให้ตราประทับทับซ้อนกัน" },
    ],
    synonyms: ["naati กับ notary ต่างกัน", "แปลเอกสาร naati ต้องผ่านทนายไหม", "sworn translator ไทย", "certified translation thailand"],
  },
  {
    slug: "notary-vs-อำเภอ-เขต",
    title: "รับรองสำเนาที่อำเภอ/เขต กับ ให้ทนาย Notary รับรอง ต่างกันอย่างไร",
    description:
      "เทียบการรับรองสำเนาเอกสารโดยนายทะเบียนที่อำเภอหรือสำนักงานเขต กับคำรับรองโดยทนาย ว่าปลายทางแบบไหนรับแบบใด และเลือกอย่างไรไม่ให้เสียเวลา",
    h1: "รับรองที่อำเภอ/เขต vs ทนาย Notary — เลือกอย่างไร",
    aLabel: "รับรองโดยอำเภอ / สำนักงานเขต",
    bLabel: "รับรองโดยทนาย Notary",
    answer:
      "อำเภอหรือสำนักงานเขตออกสำเนาที่รับรองจากทะเบียนราษฎรของตนเอง เหมาะกับการใช้ในประเทศและเป็นต้นทางของสายรับรองไปกงสุล ส่วนทนายรับรองลายมือชื่อและสำเนาของเอกสารเอกชนที่หน่วยงานราชการไม่ได้ออกให้ เช่น POA หรือหนังสือยินยอม สองอย่างนี้ใช้แทนกันไม่ได้ ต้องเลือกตามชนิดของเอกสารและปลายทาง",
    rows: [
      { aspect: "ขอบเขตเอกสาร", a: "เฉพาะเอกสารทะเบียนราษฎรที่หน่วยงานนั้นถือข้อมูล", b: "เอกสารเอกชนและสำเนาที่ผู้ขอนำต้นฉบับมาแสดง" },
      { aspect: "ตัวอย่างเอกสาร", a: "ทะเบียนบ้าน สูติบัตร ทะเบียนสมรส มรณบัตร", b: "POA, Affidavit, สำเนาพาสปอร์ต, สัญญา, เอกสารบริษัท" },
      { aspect: "ใช้ต่อกับกงสุลได้ไหม", a: "ได้ และเป็นเส้นทางมาตรฐานของเอกสารราชการ", b: "ได้ เมื่อทนายจดแจ้งลายมือชื่อไว้กับกรมการกงสุล" },
      { aspect: "ค่าธรรมเนียม", a: "ตามอัตราของหน่วยงาน โดยทั่วไปหลักสิบบาทต่อฉบับ", b: "ค่าวิชาชีพทนายตามความซับซ้อนของเอกสาร" },
      { aspect: "ความยืดหยุ่นด้านเวลา", a: "ตามเวลาราชการและคิวหน้าเคาน์เตอร์", b: "นัดหมายได้ รวมถึงบริการนอกสถานที่" },
      { aspect: "ภาษาของคำรับรอง", a: "ภาษาไทย", b: "จัดทำถ้อยคำรับรองภาษาอังกฤษได้ตามที่ปลายทางกำหนด" },
    ],
    chooseA: [
      "ต้องการสำเนาเอกสารทะเบียนราษฎรฉบับรับรองเพื่อใช้ในไทย",
      "จะนำเอกสารราชการเข้าสู่การรับรองที่กรมการกงสุล",
      "ปลายทางระบุให้ใช้สำเนาที่นายทะเบียนรับรอง",
    ],
    chooseB: [
      "เป็นเอกสารเอกชนที่ราชการไม่ได้เป็นผู้ออก",
      "ปลายทางต่างประเทศระบุคำว่า notarised หรือ certified by a lawyer",
      "ต้องมีพยานเห็นการลงลายมือชื่อ",
    ],
    faqs: [
      { q: "ทนายรับรองสำเนาทะเบียนบ้านแทนอำเภอได้ไหม?", a: "ทนายรับรองได้ว่าสำเนาตรงกับต้นฉบับที่นำมาแสดง แต่หากปลายทางระบุให้ใช้สำเนาที่นายทะเบียนรับรอง จะต้องขอจากอำเภอหรือเขตเท่านั้น " + TAIL },
      { q: "เอกสารเก่าที่อำเภอออกไว้นานแล้วใช้ได้ไหม?", a: "ขึ้นกับข้อกำหนดของผู้รับ หลายหน่วยงานกำหนดอายุเอกสารไม่เกิน 3-6 เดือน จึงควรขอฉบับใหม่ก่อนเริ่มสายรับรอง" },
      { q: "ต้องแปลก่อนหรือรับรองก่อน?", a: "โดยทั่วไปขอเอกสารฉบับรับรองจากหน่วยงานก่อน แล้วจึงแปลและเข้าสู่ขั้นรับรองถัดไป เพื่อให้คำแปลตรงกับฉบับที่จะยื่นจริง" },
    ],
    synonyms: ["รับรองสำเนาที่อำเภอ", "เขตรับรองเอกสาร", "certified copy ทะเบียนบ้าน", "notary รับรองสำเนา"],
  },
  {
    slug: "e-notary-vs-ลายเซ็นสด",
    title: "e-Notary / RON กับ การเซ็นต่อหน้าทนาย ใช้ในไทยได้แค่ไหน",
    description:
      "อธิบายสถานะของการรับรองเอกสารทางออนไลน์ในไทย เทียบกับการลงลายมือชื่อต่อหน้าทนาย พร้อมทางเลือกที่ใช้ได้จริงสำหรับผู้ที่อยู่ต่างประเทศ",
    h1: "e-Notary / RON vs การเซ็นต่อหน้าทนาย",
    aLabel: "e-Notary / Remote Online Notarization",
    bLabel: "ลงลายมือชื่อต่อหน้าทนาย (wet signature)",
    answer:
      "ในประเทศไทยการรับรองลายมือชื่อยังยึดหลักการลงนามต่อหน้าทนาย การรับรองผ่านวิดีโอคอลแบบ RON ที่ใช้ในบางรัฐของสหรัฐยังไม่มีกลไกรองรับในสายรับรองของไทย ผู้ที่อยู่ต่างประเทศจึงมักใช้ทางเลือกคือรับรองที่สถานเอกอัครราชทูตไทย หรือรับรองในประเทศที่พำนักแล้วนำผลกลับเข้าสายรับรองของปลายทาง",
    rows: [
      { aspect: "การยอมรับในไทย", a: "ยังไม่มีกลไกรองรับในสายรับรองเอกสารของไทย", b: "เป็นแนวปฏิบัติมาตรฐาน" },
      { aspect: "การพิสูจน์ตัวตน", a: "ผ่านระบบออนไลน์และการบันทึกวิดีโอ", b: "ตรวจบัตรประชาชนหรือพาสปอร์ตตัวจริงต่อหน้า" },
      { aspect: "การนำไปรับรองต่อที่กงสุล", a: "มีความเสี่ยงถูกปฏิเสธเพราะเทียบลายมือชื่อไม่ได้", b: "ทำได้เมื่อทนายจดแจ้งลายมือชื่อไว้" },
      { aspect: "เหมาะกับใคร", a: "ผู้ใช้เอกสารภายในเขตอำนาจที่มีกฎหมาย RON รองรับ", b: "ผู้ที่ต้องใช้เอกสารกับหน่วยงานไทยหรือสถานทูตในไทย" },
      { aspect: "ทางเลือกเมื่ออยู่ต่างประเทศ", a: "ตรวจว่ากฎหมายของประเทศนั้นรองรับหรือไม่", b: "รับรองที่สถานเอกอัครราชทูต/สถานกงสุลไทย หรือผู้รับรองในประเทศที่พำนัก" },
      { aspect: "สิ่งที่ทำออนไลน์ได้แน่นอน", a: "ปรึกษา ตรวจร่างเอกสาร นัดหมาย ส่งไฟล์ล่วงหน้า", b: "ขั้นตอนลงนามจริงทำที่สำนักงานหรือแบบนอกสถานที่" },
    ],
    chooseA: [
      "เอกสารใช้เฉพาะในเขตอำนาจที่มีกฎหมายรองรับ RON",
      "ผู้รับเอกสารยืนยันเป็นลายลักษณ์อักษรว่ารับรูปแบบออนไลน์",
      "ไม่ต้องนำเอกสารเข้าสายรับรองของไทยต่อ",
    ],
    chooseB: [
      "ต้องยื่นต่อกรมการกงสุลหรือสถานทูตในไทย",
      "เอกสารมีผลผูกพันทางกฎหมาย เช่น มอบอำนาจขายที่ดิน",
      "ต้องการลดความเสี่ยงถูกตีกลับให้มากที่สุด",
    ],
    faqs: [
      { q: "เซ็นผ่าน Zoom แล้วให้ทนายรับรองย้อนหลังได้ไหม?", a: "ไม่ควรทำ เพราะถ้อยคำรับรองระบุว่าผู้ลงนามลงลายมือชื่อต่อหน้าทนาย การรับรองที่ไม่ตรงข้อเท็จจริงกระทบทั้งความน่าเชื่อถือของเอกสารและความรับผิดของผู้เกี่ยวข้อง " + TAIL },
      { q: "อยู่ต่างประเทศต้องบินกลับไทยไหม?", a: "ส่วนใหญ่ไม่ต้อง ใช้บริการรับรองที่สถานเอกอัครราชทูตหรือสถานกงสุลไทยในประเทศที่พำนัก หรือรับรองโดยผู้มีอำนาจในประเทศนั้นแล้วนำเข้าสายรับรองต่อ" },
      { q: "ลายเซ็นดิจิทัลตามกฎหมายธุรกรรมทางอิเล็กทรอนิกส์ใช้แทนได้ไหม?", a: "ลายมือชื่ออิเล็กทรอนิกส์ใช้ได้ในธุรกรรมหลายประเภทตามกฎหมายไทย แต่คนละเรื่องกับการรับรองลายมือชื่อโดยทนายเพื่อใช้ต่างประเทศ ให้ยึดข้อกำหนดของผู้รับเอกสารเป็นหลัก" },
    ],
    synonyms: ["e-notary ไทย", "remote online notarization thailand", "notary ออนไลน์ ได้ไหม", "เซ็นเอกสารจากต่างประเทศ"],
  },
  {
    slug: "notary-vs-สถานทูตปลายทาง",
    title: "รับรองโดยทนาย กับ รับรองที่สถานทูตปลายทาง ต่างกันอย่างไร",
    description:
      "เทียบชั้นสุดท้ายของสายรับรองเอกสาร ว่าสถานทูตปลายทางรับรองอะไร ต่างจากทนายอย่างไร และกรณีใดที่ข้ามขั้นสถานทูตได้",
    h1: "ทนาย Notary vs สถานทูตประเทศปลายทาง",
    aLabel: "ทนาย Notary",
    bLabel: "สถานทูต/สถานกงสุลปลายทาง",
    answer:
      "ทนายเป็นชั้นต้นของสายรับรอง ส่วนสถานทูตปลายทางเป็นชั้นสุดท้ายที่ยืนยันว่าลายมือชื่อของเจ้าหน้าที่กรมการกงสุลไทยเป็นของจริง เพื่อให้เอกสารใช้ได้ในประเทศนั้น ลำดับที่ถูกต้องคือทนายหรือผู้ออกเอกสาร แล้วกรมการกงสุล แล้วจึงสถานทูต บางประเทศยกเว้นขั้นสถานทูต จึงต้องตรวจเงื่อนไขรายประเทศก่อนเริ่ม",
    rows: [
      { aspect: "ลำดับในสายรับรอง", a: "ชั้นแรก", b: "ชั้นสุดท้ายก่อนนำไปใช้ในต่างประเทศ" },
      { aspect: "รับรองอะไร", a: "ลายมือชื่อ สำเนา คำสาบานต่อหน้าทนาย", b: "ลายมือชื่อและตราของกรมการกงสุลไทย" },
      { aspect: "ค่าธรรมเนียม", a: "ค่าวิชาชีพทนาย", b: "อัตราของแต่ละสถานทูต แตกต่างกันมาก ต้องตรวจกับสถานทูตโดยตรง" },
      { aspect: "ระยะเวลา", a: "ภายในวันเดียวหากเอกสารพร้อม", b: "ตามคิวและนโยบายของสถานทูตแต่ละแห่ง อาจหลายวันถึงหลายสัปดาห์" },
      { aspect: "ข้อกำหนดคำแปล", a: "จัดทำถ้อยคำรับรองสองภาษาได้", b: "หลายแห่งกำหนดภาษาของคำแปลและรูปแบบเฉพาะ" },
      { aspect: "ข้ามขั้นได้ไหม", a: "ข้ามไม่ได้เมื่อเป็นเอกสารเอกชน", b: "ข้ามได้เมื่อประเทศปลายทางไม่กำหนด หรือเมื่อ Apostille มีผลใช้กับไทย" },
    ],
    chooseA: [
      "เริ่มต้นกระบวนการกับเอกสารเอกชน",
      "ต้องมีผู้รับรองลายมือชื่อก่อนเข้าสู่กงสุล",
      "ปลายทางขอเพียงเอกสาร notarised",
    ],
    chooseB: [
      "ประเทศปลายทางกำหนดให้ต้องมีตรารับรองของสถานทูต",
      "ใช้เอกสารกับหน่วยงานราชการหรือศาลในประเทศนั้น",
      "ผู้รับเอกสารระบุรูปแบบการรับรองเป็นลายลักษณ์อักษร",
    ],
    faqs: [
      { q: "ยื่นสถานทูตก่อนกงสุลได้ไหม?", a: "ไม่ได้ สถานทูตตรวจสอบลายมือชื่อของเจ้าหน้าที่กรมการกงสุล จึงต้องผ่านกรมการกงสุลมาก่อนเสมอ " + TAIL },
      { q: "ค่าธรรมเนียมสถานทูตเท่าไร?", a: "แต่ละสถานทูตกำหนดอัตราของตนเองและปรับเปลี่ยนได้ตลอด เราจะตรวจอัตราล่าสุดกับสถานทูตที่เกี่ยวข้องให้ก่อนเริ่มงานทุกครั้ง" },
      { q: "หลังไทยใช้ Apostille แล้วยังต้องผ่านสถานทูตไหม?", a: "เมื่ออนุสัญญามีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2027 การใช้เอกสารระหว่างรัฐภาคีจะใช้ Apostille แทนการรับรองที่สถานทูตได้ ส่วนประเทศที่ไม่ใช่ภาคียังใช้สายเดิม" },
    ],
    synonyms: ["รับรองเอกสาร สถานทูต", "legalization embassy bangkok", "ลำดับรับรองเอกสาร ต่างประเทศ", "notary แล้วไปไหนต่อ"],
  },
];

export const NOTARY_COMPARE_BY_SLUG: Record<string, NotaryCompare> = Object.fromEntries(
  NOTARY_COMPARE.map((c) => [c.slug, c]),
);
